import { useEffect, useState, type MouseEvent } from "react";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface GmailInboxFilterValues {
  dateFrom?: string;
  dateTo?: string;
}

interface GmailDateFilterProps {
  disabled?: boolean;
  onChange: (values: GmailInboxFilterValues) => void;
}

function toIsoDate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

function formatDisplayDate(iso?: string): string {
  if (!iso) return "";
  try {
    return format(new Date(iso + "T12:00:00"), "MMM d, yyyy");
  } catch {
    return iso;
  }
}

export function GmailDateFilter({ disabled = false, onChange }: GmailDateFilterProps) {
  const [rangeMode, setRangeMode] = useState(false);
  const [singleDate, setSingleDate] = useState<Date | undefined>();
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    let dateFrom: string | undefined;
    let dateTo: string | undefined;

    if (rangeMode) {
      if (dateRange?.from) dateFrom = toIsoDate(dateRange.from);
      if (dateRange?.to) dateTo = toIsoDate(dateRange.to);
      else if (dateRange?.from) dateTo = dateFrom;
    } else if (singleDate) {
      dateFrom = toIsoDate(singleDate);
      dateTo = dateFrom;
    }

    onChange({ dateFrom, dateTo });
  }, [rangeMode, singleDate, dateRange, onChange]);

  const hasDateFilter = Boolean(singleDate || dateRange?.from);

  const dateLabel = rangeMode
    ? dateRange?.from
      ? dateRange.to
        ? `${formatDisplayDate(toIsoDate(dateRange.from))} – ${formatDisplayDate(toIsoDate(dateRange.to))}`
        : formatDisplayDate(toIsoDate(dateRange.from))
      : "Date range"
    : singleDate
      ? formatDisplayDate(toIsoDate(singleDate))
      : "Date";

  const handleClear = (e: MouseEvent) => {
    e.stopPropagation();
    setSingleDate(undefined);
    setDateRange(undefined);
    setRangeMode(false);
  };

  return (
    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-9 justify-start text-left font-normal min-w-[140px] max-w-[220px]",
            !hasDateFilter && "text-muted-foreground"
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate">{dateLabel}</span>
          {hasDateFilter && (
            <X
              className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50 hover:opacity-100"
              onClick={handleClear}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex gap-1 border-b p-2">
          <Button
            type="button"
            variant={!rangeMode ? "secondary" : "ghost"}
            size="sm"
            className="h-7 flex-1 text-xs"
            onClick={() => {
              setRangeMode(false);
              setDateRange(undefined);
            }}
          >
            Single date
          </Button>
          <Button
            type="button"
            variant={rangeMode ? "secondary" : "ghost"}
            size="sm"
            className="h-7 flex-1 text-xs"
            onClick={() => {
              setRangeMode(true);
              setSingleDate(undefined);
            }}
          >
            Date range
          </Button>
        </div>
        {rangeMode ? (
          <Calendar
            mode="range"
            selected={dateRange}
            onSelect={(range) => {
              setDateRange(range);
              if (range?.from && range?.to) setCalendarOpen(false);
            }}
            numberOfMonths={2}
            pagedNavigation
            defaultMonth={dateRange?.from ?? dateRange?.to ?? new Date()}
            initialFocus
          />
        ) : (
          <Calendar
            mode="single"
            selected={singleDate}
            onSelect={(date) => {
              setSingleDate(date);
              if (date) setCalendarOpen(false);
            }}
            defaultMonth={singleDate ?? new Date()}
            initialFocus
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

/** @deprecated Use GmailDateFilter instead */
export const GmailInboxFilters = GmailDateFilter;
